import React, { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlusCircle,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faImage,
  faNewspaper,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./DoctorPortal.module.css";

const BlogManager = ({
  blogs = [],
  newBlogData,
  handleBlogInputChange,
  submitNewBlog,
  editingBlogId,
  editingBlogData,
  startEditBlog,
  cancelEditBlog,
  submitEditBlog,
  deleteBlog,
}) => {
  const currentData = editingBlogId ? editingBlogData : newBlogData;
  const fileInputRef = useRef(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleBlogInputChange("image", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div>
      {/* View Header */}
      <div className={styles.viewHeader}>
        <div>
          <h1 className={styles.viewTitle}>Clinical Blog Authoring</h1>
          <p className={styles.viewSubtitle}>
            Publish health articles, wellness guidance, and clinic news
          </p>
        </div>
      </div>

      {/* Top Section: Authoring Form */}
      <div className={styles.glassCard} style={{ marginBottom: "2.5rem" }}>
        <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", margin: "0 0 1.25rem 0", display: "flex", alignItems: "center", gap: "8px" }}>
          <FontAwesomeIcon icon={editingBlogId ? faEdit : faPlusCircle} style={{ color: "#2d6a4f" }} />
          <span>{editingBlogId ? "Edit Published Post" : "Create New Blog Post"}</span>
        </h3>

        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div>
            <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>
              Article Title *
            </label>
            <input
              type="text"
              placeholder="e.g. Understanding Seasonal Allergies and Prevention"
              value={currentData.headTitle}
              onChange={(e) => handleBlogInputChange("headTitle", e.target.value)}
              className={styles.inputField}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "8px" }}>
              Cover Image (Upload file or paste URL)
            </label>
            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                className={styles.primaryBtn}
                style={{ padding: "0.55rem 1.25rem", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "8px", background: "#2d6a4f", color: "#ffffff", border: "none", borderRadius: "9999px", cursor: "pointer", fontWeight: 700 }}
              >
                <FontAwesomeIcon icon={faUpload} />
                <span>Upload Image</span>
              </button>

              <span style={{ fontSize: "0.85rem", color: "#94a3b8", fontWeight: 600 }}>OR</span>

              <div style={{ flex: 1, minWidth: "240px", position: "relative" }}>
                <FontAwesomeIcon icon={faImage} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "1.05rem" }} />
                <input
                  type="text"
                  placeholder="Paste image URL (https://...)"
                  value={currentData.image || ""}
                  onChange={(e) => handleBlogInputChange("image", e.target.value)}
                  className={styles.inputField}
                  style={{ paddingLeft: "2.5rem" }}
                />
              </div>
            </div>

            {/* Live Image Preview */}
            {currentData.image && (
              <div style={{ marginTop: "14px", position: "relative", display: "inline-block", borderRadius: "12px", overflow: "hidden", border: "2px solid #e2e8f0", maxHeight: "200px", boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }}>
                <img src={currentData.image} alt="Blog Cover Preview" style={{ display: "block", maxHeight: "200px", maxWidth: "100%", objectFit: "cover" }} />
                <button
                  type="button"
                  onClick={() => handleBlogInputChange("image", "")}
                  style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(239, 68, 68, 0.9)", color: "#ffffff", border: "none", borderRadius: "50%", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s" }}
                  title="Remove Image"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}
          </div>

          <div>
            <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>
              Article Content *
            </label>
            <textarea
              rows={6}
              placeholder="Write comprehensive health recommendations or insights here..."
              value={currentData.body}
              onChange={(e) => handleBlogInputChange("body", e.target.value)}
              className={styles.inputField}
              style={{ resize: "vertical", fontFamily: "inherit", lineHeight: "1.6" }}
            />
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "0.5rem" }}>
            {editingBlogId && (
              <button
                type="button"
                onClick={cancelEditBlog}
                className={styles.ghostBtn}
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>Cancel Edit</span>
              </button>
            )}
            <button
              type="button"
              onClick={editingBlogId ? submitEditBlog : submitNewBlog}
              className={styles.primaryBtn}
            >
              <FontAwesomeIcon icon={editingBlogId ? faSave : faPlusCircle} />
              <span>{editingBlogId ? "Save Changes" : "Publish Article"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Section: Masonry Grid */}
      <div>
        <h3 style={{ fontSize: "1.35rem", fontWeight: 850, color: "#0f172a", margin: "0 0 1rem 0" }}>
          Published Articles ({blogs.length})
        </h3>

        {blogs.length === 0 ? (
          <div className={styles.glassCard} style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748b" }}>
            <FontAwesomeIcon icon={faNewspaper} style={{ fontSize: "2.5rem", color: "#cbd5e1", marginBottom: "1rem" }} />
            <h3 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", margin: 0 }}>
              No articles published yet
            </h3>
            <p style={{ margin: "6px 0 0 0", fontSize: "0.92rem" }}>
              Use the authoring form above to share clinical knowledge with patients.
            </p>
          </div>
        ) : (
          <div className={styles.masonryGrid}>
            {blogs.map((blog) => {
              const dateStr = new Date(blog.createdAt || Date.now()).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              });

              return (
                <div key={blog._id} className={styles.blogCard}>
                  {blog.image ? (
                    <img src={blog.image} alt={blog.headTitle} className={styles.blogImage} />
                  ) : (
                    <div className={styles.blogImage} style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #dcfce7, #bbf7d0)", color: "#2d6a4f", fontWeight: 800, fontSize: "1.1rem" }}>
                      ReformMe Clinic
                    </div>
                  )}

                  <div className={styles.blogBody}>
                    <span style={{ fontSize: "0.78rem", fontWeight: 750, color: "#2d6a4f", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {dateStr}
                    </span>
                    <h4 style={{ fontSize: "1.15rem", fontWeight: 800, color: "#0f172a", margin: "8px 0", lineHeight: "1.4" }}>
                      {blog.headTitle}
                    </h4>
                    <p style={{ fontSize: "0.9rem", color: "#64748b", margin: "0 0 1.5rem 0", lineHeight: "1.6", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {blog.body}
                    </p>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "auto", paddingTop: "1rem", borderTop: "1px solid #f1f5f9" }}>
                      <button
                        type="button"
                        onClick={() => startEditBlog(blog)}
                        className={styles.ghostBtn}
                        style={{ padding: "0.45rem 0.9rem" }}
                        title="Edit Article"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        <span>Edit</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteBlog(blog._id)}
                        style={{ background: "rgba(239, 68, 68, 0.08)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.25)", borderRadius: "10px", padding: "0.45rem 0.9rem", cursor: "pointer", fontWeight: 700, fontSize: "0.85rem", transition: "all 0.2s" }}
                        title="Delete Article"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogManager;
